import { http } from '@/shared/api/api-client';
import type { PagedResponse } from '@/shared/api/api-types';

import type {
  Category,
  CategoryListParams,
  CreateCategoryPayload,
  UpdateCategoryPayload,
} from '../types/category.types';

/**
 * Category HTTP calls — all against the Admin API (UT system
 * `AdminApis/Catalogue`), which enforces permissions and scope and audits
 * every change. One named function per endpoint (§12).
 */

export interface CategoryOption {
  id: number;
  name: string;
  isActive: boolean;
  /**
   * The storefront brand list (`GET /Brands`) filters by translation language,
   * so a brand in a category without an English translation never appears
   * there. The brand form offers only categories that have one.
   */
  hasEnglishTranslation: boolean;
}

interface AdminPage<T> {
  items: T[];
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
}

/** The Admin API's largest page; the catalogue has a handful of categories. */
const MAX_PAGE_SIZE = 100;

/** The table's sort keys, mapped to the Admin API's whitelist. */
const SORTS: Record<string, string> = {
  nameEn: 'name',
  sortOrder: 'position',
};

/** Paged, searched, filtered and sorted on the server. */
export async function getCategories(
  params: CategoryListParams,
  signal?: AbortSignal,
): Promise<PagedResponse<Category>> {
  const page = await http.get<AdminPage<Category>>('/admin/categories', {
    params: {
      page: params.page,
      pageSize: params.pageSize,
      search: params.search,
      isActive: params.isActive,
      sort: (params.sort && SORTS[params.sort]) || 'name',
      descending: params.direction === 'desc',
    },
    signal,
  });
  return {
    items: page.items,
    page: page.page,
    pageSize: page.pageSize,
    totalItems: page.totalCount,
    totalPages: page.totalPages,
  };
}

/**
 * Every category, for pickers. Inactive ones are included: a brand may belong
 * to an inactive category, and its form must still show it.
 */
export async function getCategoryOptions(signal?: AbortSignal): Promise<CategoryOption[]> {
  const page = await http.get<AdminPage<Category>>('/admin/categories', {
    params: { page: 1, pageSize: MAX_PAGE_SIZE },
    signal,
  });
  return page.items.map((category) => ({
    id: category.id,
    name: category.nameEn ?? category.nameAr ?? `Category ${category.id}`,
    isActive: category.isActive,
    hasEnglishTranslation: category.hasEnglishTranslation ?? true,
  }));
}

/** Returns the new category's id. */
export async function createCategory(payload: CreateCategoryPayload): Promise<number> {
  return http.post<number>('/admin/categories', payload);
}

export async function updateCategory(
  categoryId: number,
  payload: UpdateCategoryPayload,
): Promise<void> {
  await http.put<number>(`/admin/categories/${categoryId}`, payload);
}

/** Returns how many records changed (0 when already in that state). */
export async function setCategoryStatus(
  categoryId: number,
  isActive: boolean,
  reason: string | null,
): Promise<number> {
  const result = await http.post<{ changed: number }>(`/admin/categories/${categoryId}/status`, {
    isActive,
    reason,
  });
  return result.changed;
}

/**
 * Deletes a category no brand belongs to. The Admin API refuses one still in
 * use with 409 `CATEGORY_IN_USE` (checked again under a lock at delete time).
 */
export async function deleteCategory(categoryId: number, reason: string | null): Promise<void> {
  await http.delete<number>(`/admin/categories/${categoryId}`, { data: { reason } });
}

/** Every category in storefront order: saved positions first, the rest by id. */
export async function getCategoryOrder(signal?: AbortSignal): Promise<Category[]> {
  return http.get<Category[]>('/admin/categories/order', { signal });
}

/**
 * Saves the storefront order (catalogue.manage, all-country scope): every
 * category exactly once, first to last. Audited with the reason.
 */
export async function saveCategoryOrder(categoryIds: number[], reason: string): Promise<number> {
  const result = await http.put<{ changed: number }>('/admin/categories/order', {
    categoryIds,
    reason,
  });
  return result.changed;
}
